import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Code,
  Globe,
  Download,
  Copy,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  Sparkles,
  FileText,
  Zap,
  Edit3,
  ChevronDown,
  ExternalLink,
  Settings,
  Upload,
  ArrowLeft,
  Server,
  ShoppingBag,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import type { User } from "@supabase/supabase-js";

interface WebsiteData {
  id: string;
  domain: string;
  title: string;
  favicon_url: string;
}

interface LLMsTxtGeneratorProps {
  user: User;
  website: WebsiteData;
  isActive?: boolean;
}

export const LLMsTxtGenerator: React.FC<LLMsTxtGeneratorProps> = ({ user, website, isActive = true }) => {
  // Use localStorage key for this user/website combo
  const storageKey = `llms_txt_${user.id}_${website.id}`;
  
  // Load persisted state from localStorage
  const loadPersistedState = () => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          urlInput: parsed.urlInput || (website.domain ? `https://${website.domain}` : ""),
          generating: parsed.generating || false,
          llmsTxtContent: parsed.llmsTxtContent || "",
          showContent: parsed.showContent || false
        };
      }
    } catch (e) {
      console.error('Error loading persisted state:', e);
    }
    return {
      urlInput: website.domain ? `https://${website.domain}` : "",
      generating: false,
      llmsTxtContent: "",
      showContent: false
    };
  };

  const persistedState = loadPersistedState();
  
  const [urlInput, setUrlInput] = useState(persistedState.urlInput);
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [generating, setGenerating] = useState(persistedState.generating);
  const [llmsTxtContent, setLlmsTxtContent] = useState<string>(persistedState.llmsTxtContent);
  const [copied, setCopied] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [savedContent, setSavedContent] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showContent, setShowContent] = useState(persistedState.showContent);
  const [initialized, setInitialized] = useState(false);
  
  // Track if data has been loaded initially
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        urlInput,
        generating,
        llmsTxtContent,
        showContent
      }));
    } catch (e) {
      console.error('Error persisting state:', e);
    }
  }, [urlInput, generating, llmsTxtContent, showContent, storageKey]);

  // Load saved content on component mount - only when active
  useEffect(() => {
    if (!isActive) return;
    
    const key = `${user.id}-${website.id}`;
    if (key !== loadedKey) {
      loadSavedContent();
      setLoadedKey(key);
    }
  }, [user.id, website.id, isActive, loadedKey]);

  // Don't reset state when switching tabs - preserve generating state and content
  // Only collapse interactive elements
  useEffect(() => {
    return () => {
      setOpenFAQ(null);
      setSelectedPlatform(null);
      // Don't reset generating state or content - preserve them
    };
  }, []);

  const loadSavedContent = async () => {
    try {
      const { data, error } = await supabase
        .from('llms_txt_storage')
        .select('*')
        .eq('user_id', user.id)
        .eq('website_id', website.id)
        .order('generated_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 means no rows found
        console.error('Error loading saved content:', error);
        return;
      }

      if (data) {
        setSavedContent(data);
        setLlmsTxtContent(data.content);
        setUrlInput(data.url);
        setShowContent(true);
      }
    } catch (error) {
      console.error('Error loading saved content:', error);
    } finally {
      setInitialized(true);
    }
  };

  const saveContent = async (content: string, url: string) => {
    try {
      const { error } = await supabase
        .from('llms_txt_storage')
        .upsert({
          user_id: user.id,
          website_id: website.id,
          url: url,
          content: content,
          generated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error saving content:', error);
        return;
      }

      // Reload saved content to get the updated data
      await loadSavedContent();
    } catch (error) {
      console.error('Error saving content:', error);
    }
  };

  const canRegenerate = () => {
    if (!savedContent) return true;
    
    const generatedDate = new Date(savedContent.generated_at);
    const now = new Date();
    const daysDiff = (now.getTime() - generatedDate.getTime()) / (1000 * 60 * 60 * 24);
    
    return daysDiff >= 14;
  };

  const generateLLMsTxt = async () => {
    if (!urlInput.trim()) {
      toast.error("Please enter a website URL");
      return;
    }

    setGenerating(true);
    setLlmsTxtContent("");
    
    // Show initial loading toast
    const loadingToast = toast.loading("Generating llms.txt file... This may take a minute.");

    try {
      // Get auth token for Supabase Edge Function
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      console.log(`[LLMs.txt] Starting generation for URL: ${urlInput.trim()}`);
      
      // Add a timeout wrapper (65 seconds - catch Supabase function timeout)
      let timeoutId: NodeJS.Timeout | null = null;
      let hasTimedOut = false;
      
      const timeoutPromise = new Promise<{ data: null, error: { message: string } }>((resolve) => {
        timeoutId = setTimeout(() => {
          hasTimedOut = true;
          console.error('[LLMs.txt] Frontend timeout reached - function taking too long');
          resolve({ 
            data: null, 
            error: { message: 'Request timeout - The service is taking longer than expected. Please try again in a moment.' } 
          });
        }, 65000); // 65 seconds timeout (before Supabase's 60s limit + buffer)
      });

      // Use Supabase Edge Function instead of Vercel API
      const functionPromise = supabase.functions.invoke('generate-llms-txt', {
        body: {
          url: urlInput.trim()
        }
      }).then((result) => {
        // Clear timeout if function completes
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (hasTimedOut) {
          // If we already timed out, don't process the result
          return { data: null, error: { message: 'Request timeout' } };
        }
        return result;
      }).catch((err) => {
        // Clear timeout on error
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        throw err;
      });

      // Race between function call and timeout
      const result = await Promise.race([
        functionPromise,
        timeoutPromise
      ]);

      // Clear timeout if still set
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      const { data, error } = result;

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      if (error) {
        console.error('[LLMs.txt] Supabase function error:', error);
        
        // Check if it's a timeout or retry-related error
        const errorMsg = error.message || 'Failed to generate llms.txt';
        if (errorMsg.includes('timeout') || errorMsg.includes('Request timeout') || errorMsg.includes('taking longer')) {
          toast.error(
            "The service is experiencing high traffic and requests are taking longer than expected. Please try again in a few moments.",
            { duration: 8000 }
          );
          setGenerating(false);
          return; // Don't throw - just stop here
        }
        throw new Error(errorMsg);
      }

      if (!data || !data.success) {
        const errorMsg = data?.error || data?.details || 'Failed to generate llms.txt';
        const wasRetried = data?.retried === true;
        console.error('[LLMs.txt] Function returned error:', errorMsg);
        
        // Show user-friendly error messages based on error type
        if (wasRetried || errorMsg.includes('timeout') || errorMsg.includes('Request timeout') || errorMsg.includes('slow')) {
          toast.error(
            wasRetried 
              ? "We automatically retried multiple times, but the service is still experiencing high traffic. Please try again in a moment."
              : "The service is experiencing high traffic. We're automatically retrying... Please wait.",
            { duration: 6000 }
          );
        } else if (errorMsg.includes('temporarily unavailable') || errorMsg.includes('circuit breaker')) {
          toast.error(
            "Service temporarily unavailable due to high traffic. Please try again in a few minutes.",
            { duration: 6000 }
          );
        } else {
          // Use the details field if available (it has the user-friendly message)
          const displayMsg = data?.details || errorMsg;
          toast.error(
            displayMsg.includes('high traffic') || displayMsg.includes('experiencing')
              ? displayMsg
              : `Unable to generate llms.txt. ${displayMsg}`,
            { duration: 5000 }
          );
        }
        throw new Error(errorMsg);
      }

      console.log(`[LLMs.txt] Generation successful, content length: ${data.content?.length || 0}`);

      // Use the main content (llms.txt format)
      setLlmsTxtContent(data.content || '');
      setShowContent(true);
      
      // Save the generated content
      await saveContent(data.content || '', urlInput.trim());
      
      toast.success(`LLMs.txt generated successfully! Content length: ${data.contentLength || data.content?.length || 0} characters.`);
      
    } catch (error: any) {
      console.error('Error generating llms.txt:', error);
      
      // Dismiss loading toast if still showing
      toast.dismiss(loadingToast);
      
      const errorMsg = error?.message || 'Failed to generate llms.txt';
      
      // Show user-friendly error messages
      if (errorMsg.includes('timeout') || errorMsg.includes('Request timeout') || errorMsg.includes('taking longer')) {
        toast.error(
          "The service is experiencing high traffic and requests are taking longer than expected. Please try again in a few moments.",
          { duration: 8000 }
        );
      } else if (errorMsg.includes('retrying')) {
        // Don't show error if we're retrying - the retry toast already showed
        return;
      } else {
        toast.error(
          `Unable to generate llms.txt. ${errorMsg.includes('high traffic') || errorMsg.includes('slow') || errorMsg.includes('experiencing') ? 'The service may be experiencing high demand. Please try again in a moment.' : 'Please try again.'}`,
          { duration: 6000 }
        );
      }
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(llmsTxtContent);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const downloadAsFile = () => {
    const blob = new Blob([llmsTxtContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'llms.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("File downloaded as llms.txt!");
  };

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <Card className="
        w-full 
        p-10 
        flex flex-col md:flex-row items-center gap-10 
        bg-gradient-to-br from-primary/8 via-primary/5 to-primary/12 
        border border-primary/30 
        rounded-3xl
        shadow-[0_4px_20px_rgba(59,130,246,0.06),0_2px_8px_rgba(59,130,246,0.04)]
        hover:shadow-[0_8px_32px_rgba(59,130,246,0.1),0_4px_16px_rgba(59,130,246,0.06)]
        transition-all duration-500 ease-out
        backdrop-blur-sm
      ">
        <div className="flex-1 text-center md:text-left space-y-4">
          <h2 className="text-3xl font-bold text-gray-900 leading-tight">
            Generate <span className="text-primary">LLMs.txt</span> files with just one click!
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Transform any website into an AI friendly format that helps chatbots, search engines, and AI tools understand your content perfectly. Get better AI responses about your business instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <div className="flex gap-2 flex-1">
              <div className="relative flex-1">
                <Input
                  type="url"
                  placeholder="Enter website URL (e.g., https://example.com)"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  className={`
                    flex-1 rounded-2xl border-2 transition-all duration-300 pr-12
                    ${isEditingUrl 
                      ? 'border-primary/50 bg-white text-gray-900' 
                      : 'border-gray-200 bg-gray-100 text-gray-700'
                    }
                    ${generating ? 'opacity-50' : ''}
                  `}
                  disabled={generating || !isEditingUrl}
                />
                <button
                  onClick={() => setIsEditingUrl(!isEditingUrl)}
                  disabled={generating}
                  className="
                    absolute right-3 top-1/2 transform -translate-y-1/2
                    p-1.5 rounded-lg
                    hover:bg-gray-100 transition-colors duration-200
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  <Edit3 className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>
            <Button 
              onClick={generateLLMsTxt} 
              disabled={generating || !urlInput.trim() || (!initialized ? false : (!canRegenerate() && savedContent))}
              className="
                rounded-2xl 
                px-8 py-4 
                text-lg font-semibold 
                bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground 
                hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 
                transition-all duration-300 ease-out
                shadow-[0_2px_8px_rgba(59,130,246,0.12)]
                hover:shadow-[0_4px_12px_rgba(59,130,246,0.18)]
                hover:scale-[1.02]
                min-w-[160px]
                relative overflow-hidden group
                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
              "
            >
              {generating ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Generating...
                </>
              ) : !initialized ? (
                <>
                  <Sparkles className="h-5 w-5 mr-2" />
                  Generate
                  <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                </>
              ) : savedContent && !canRegenerate() ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2" />
                  Regenerate Soon
                </>
              ) : savedContent ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2" />
                  Regenerate
                  <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5 mr-2" />
                  Generate
                  <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                </>
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
            </Button>
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center justify-center w-48 h-40 rounded-3xl bg-gradient-to-br from-primary/15 to-primary/8 border border-primary/20 shadow-[0_4px_16px_rgba(59,130,246,0.08)]">
          <Code className="w-24 h-24 text-primary drop-shadow-lg" />
        </div>
      </Card>

      {/* Results Section */}
      {initialized && showContent && llmsTxtContent && (
        <Card className="
          border-2 border-green-200/50 
          bg-gradient-to-br from-green-50/50 to-emerald-50/30
          shadow-[0_4px_20px_rgba(34,197,94,0.08),0_2px_8px_rgba(34,197,94,0.04)]
          hover:shadow-[0_8px_32px_rgba(34,197,94,0.12),0_4px_16px_rgba(34,197,94,0.06)]
          transition-all duration-500 ease-out
        ">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-primary/10">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-bold text-gray-900">
                    {savedContent ? 'LLMs.txt Ready!' : 'LLMs.txt Generated Successfully!'}
                  </CardTitle>
                  <div className="flex items-center gap-3 mt-1">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Globe className="h-4 w-4" />
                      <span>{savedContent?.url || urlInput}</span>
                    </div>
                    <div className="flex gap-2">
                      <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/80 backdrop-blur-sm text-xs font-medium text-gray-600">
                        <Zap className="h-3 w-3" />
                        AI-optimized
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/80 backdrop-blur-sm text-xs font-medium text-gray-600">
                        <FileText className="h-3 w-3" />
                        Ready to use
                      </div>
                    </div>
                  </div>
                  {savedContent && (
                    <p className="text-sm text-gray-500 mt-2">
                      Generated on {new Date(savedContent.generated_at).toLocaleDateString()}
                      {!canRegenerate() && (
                        <span className="ml-2 text-gray-600 font-medium">
                          • Regenerate in {Math.ceil(14 - ((new Date().getTime() - new Date(savedContent.generated_at).getTime()) / (1000 * 60 * 60 * 24)))} days
                        </span>
                      )}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={copyToClipboard}
                  className="
                    rounded-xl px-6 py-3 
                    border-2 border-gray-200 
                    hover:border-primary/50 hover:bg-primary/5 
                    transition-all duration-300
                    flex items-center gap-2
                  "
                >
                  {copied ? (
                    <>
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="font-semibold">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      <span className="font-semibold">Copy</span>
                    </>
                  )}
                </Button>
                <Button
                  onClick={downloadAsFile}
                  className="
                    rounded-xl px-6 py-3 
                    bg-gradient-to-r from-primary via-primary to-primary/90 
                    hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 
                    transition-all duration-300
                    shadow-[0_4px_12px_rgba(59,130,246,0.15)]
                    hover:shadow-[0_6px_20px_rgba(59,130,246,0.2)]
                    flex items-center gap-2
                  "
                >
                  <Download className="h-4 w-4" />
                  <span className="font-semibold">Download</span>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            
            <div className="relative">
              <pre className="
                bg-gray-50 p-6 rounded-2xl 
                overflow-auto max-h-96 
                text-sm whitespace-pre-wrap 
                border-2 border-gray-200/50
                font-mono leading-relaxed
                shadow-inner
              ">
                {llmsTxtContent}
              </pre>
            </div>
          </CardContent>
        </Card>
      )}

      {/* FAQ Section */}
      <div className="space-y-4">
        <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
          What is LLMs.txt?
        </h3>
        
        <div className="space-y-3">
          {[
            {
              question: "What exactly is LLMs.txt and why do I need it?",
              answer: "LLMs.txt is like a 'cheat sheet' for AI systems about your website. When someone asks an AI chatbot about your business, or when search engines try to understand your content, this file gives them a clear, organized summary of what you do, what you sell, and how you help customers. It's like having a perfect elevator pitch that AI can read and understand instantly."
            },
            {
              question: "How will this actually help my business grow?",
              answer: "When AI tools understand your business better, they give more accurate recommendations to potential customers. For example, if someone asks ChatGPT 'What's the best restaurant in my area?', and your restaurant has an LLMs.txt file, the AI can give a much better description of your food, atmosphere, and specialties. This leads to more qualified leads, better online visibility, and ultimately more customers finding and choosing your business."
            },
            {
              question: "Is this complicated to set up and maintain?",
              answer: "Not at all! Our tool generates the entire file for you in seconds. You just download it and upload it to your website once. Most website platforms make this super easy - it's usually just a few clicks. You only need to update it if you make major changes to your business (like adding new services or changing your main focus). Think of it as a one-time setup that keeps working for you."
            },
            {
              question: "Where and how do AI systems actually use this file?",
              answer: "AI chatbots like ChatGPT, search engines like Google, and other AI tools automatically look for and read LLMs.txt files when they need to understand a website. It's used in customer service chatbots, content recommendation systems, local business directories, and even when people ask AI assistants for business recommendations. The file acts as your business's 'AI business card' that gets shared across the internet."
            }
          ].map((faq, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl border transition-all duration-200 ${
                openFAQ === index 
                  ? 'border-primary/30 shadow-md' 
                  : 'border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300'
              }`}
            >
              <button
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                className={`w-full px-6 py-4 text-left flex items-center justify-between transition-colors ${
                  openFAQ === index 
                    ? 'bg-primary/5 border-b border-primary/10 rounded-t-2xl' 
                    : 'hover:bg-gray-50 rounded-2xl'
                }`}
              >
                <h4 className="text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </h4>
                <ChevronDown
                  className={`h-5 w-5 text-gray-500 transition-transform duration-200 flex-shrink-0 ${
                    openFAQ === index ? 'rotate-180 text-primary' : ''
                  }`}
                />
              </button>
              {openFAQ === index && (
                <div className="px-6 pb-4 pt-2 bg-primary/2 rounded-b-2xl">
                  <p className="text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Implementation Guide - Step by Step */}
      <Card className="
        border border-gray-200 
        bg-white
        shadow-sm
      ">
        <CardHeader>
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              How to Add LLMs.txt to Your Website
            </h3>
            <p className="text-gray-600">
              {selectedPlatform ? `Follow these steps for ${selectedPlatform}` : "Choose your platform to see the steps"}
            </p>
          </div>
        </CardHeader>
        <CardContent>
          {!selectedPlatform ? (
            // Step 1: Platform Selection
            <div className="space-y-6">
              <div className="text-center mb-8">
                <p className="text-gray-600 font-medium">Choose Your Platform</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { id: 'wordpress', name: 'WordPress', logo: 'https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/WordPress_blue_logo.svg', color: 'blue', icon: 'Settings' },
                  { id: 'shopify', name: 'Shopify', logo: 'https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/shopify-logo.svg', color: 'green', icon: 'ShoppingBag' },
                  { id: 'wix', name: 'Wix', logo: 'https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/wix.svg', color: 'purple', icon: 'Globe' },
                  { id: 'webflow', name: 'Webflow', logo: 'https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/Mark_Logo_Blue.svg', color: 'orange', icon: 'Code' },
                  { id: 'squarespace', name: 'Squarespace', logo: 'https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/6350f035c3354e78d0aad336_squarespace.png', color: 'gray', icon: 'FileText' },
                  { id: 'other', name: 'Other', logo: null, color: 'indigo', icon: 'Server' }
                ].map((platform) => (
                  <div
                    key={platform.id}
                    onClick={() => setSelectedPlatform(platform.id)}
                    className="
                      p-6 rounded-2xl bg-white border-2 border-gray-200 
                      hover:border-primary/50 hover:shadow-lg 
                      transition-all duration-300 ease-out cursor-pointer
                      hover:scale-105 active:scale-95
                    "
                  >
                    <div className="text-center space-y-3">
                      <div className="w-16 h-16 mx-auto rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center relative">
                        {platform.logo ? (
                          <>
                            <img 
                              src={platform.logo} 
                              alt={platform.name} 
                              className="w-10 h-10 object-contain"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.parentElement?.querySelector('.fallback-icon') as HTMLElement;
                                if (fallback) {
                                  fallback.style.display = 'block';
                                }
                              }}
                              onLoad={(e) => {
                                const target = e.target as HTMLImageElement;
                                const fallback = target.parentElement?.querySelector('.fallback-icon') as HTMLElement;
                                if (fallback) {
                                  fallback.style.display = 'none';
                                }
                              }}
                            />
                            <div className="fallback-icon absolute" style={{ display: 'none' }}>
                              {platform.id === 'wordpress' && <Settings className="h-8 w-8 text-gray-600" />}
                              {platform.id === 'shopify' && <ShoppingBag className="h-8 w-8 text-gray-600" />}
                              {platform.id === 'wix' && <Globe className="h-8 w-8 text-gray-600" />}
                              {platform.id === 'webflow' && <Code className="h-8 w-8 text-gray-600" />}
                              {platform.id === 'squarespace' && <FileText className="h-8 w-8 text-gray-600" />}
                            </div>
                          </>
                        ) : (
                          <div>
                            {platform.id === 'other' && <Server className="h-8 w-8 text-gray-600" />}
                          </div>
                        )}
                      </div>
                      <h4 className="font-semibold text-gray-900">{platform.name}</h4>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Step 2: Platform-specific Guide
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSelectedPlatform(null)}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <ArrowLeft className="h-5 w-5 text-gray-600" />
                  </button>
                  <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg text-gray-800 font-semibold">
                    {selectedPlatform === 'wordpress' && 'WordPress Setup'}
                    {selectedPlatform === 'shopify' && 'Shopify Setup'}
                    {selectedPlatform === 'wix' && 'Wix Setup'}
                    {selectedPlatform === 'webflow' && 'Webflow Setup'}
                    {selectedPlatform === 'squarespace' && 'Squarespace Setup'}
                    {selectedPlatform === 'other' && 'Other Platform Setup'}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="space-y-4">
                  {selectedPlatform === 'wordpress' && (
                    <>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</span>
                        <div>
                          <p className="text-gray-900 font-medium">Go to your WordPress admin dashboard</p>
                          <p className="text-gray-600 text-sm">Navigate to <strong>Media → Add New</strong></p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</span>
                        <div>
                          <p className="text-gray-900 font-medium">Upload your llms.txt file</p>
                          <p className="text-gray-600 text-sm">Select the <code className="bg-gray-100 px-1 rounded">llms.txt</code> file you downloaded</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</span>
                        <div>
                          <p className="text-gray-900 font-medium">Copy the file URL</p>
                          <p className="text-gray-600 text-sm">Copy the URL and add it to your site root directory</p>
                        </div>
                      </div>
                    </>
                  )}

                  {selectedPlatform === 'shopify' && (
                    <>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</span>
                        <div>
                          <p className="text-gray-900 font-medium">Go to your Shopify admin panel</p>
                          <p className="text-gray-600 text-sm">Navigate to <strong>Online Store → Files</strong></p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</span>
                        <div>
                          <p className="text-gray-900 font-medium">Upload your file</p>
                          <p className="text-gray-600 text-sm">Click <strong>Upload files</strong> and select your <code className="bg-gray-100 px-1 rounded">llms.txt</code></p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</span>
                        <div>
                          <p className="text-gray-900 font-medium">Your file is ready!</p>
                          <p className="text-gray-600 text-sm">It will be available at <code className="bg-gray-100 px-1 rounded">yoursite.com/llms.txt</code></p>
                        </div>
                      </div>
                    </>
                  )}

                  {selectedPlatform === 'wix' && (
                    <>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</span>
                        <div>
                          <p className="text-gray-900 font-medium">Open your Wix site editor</p>
                          <p className="text-gray-600 text-sm">Go to <strong>Settings → Custom Code</strong></p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</span>
                        <div>
                          <p className="text-gray-900 font-medium">Add your llms.txt content</p>
                          <p className="text-gray-600 text-sm">Paste the content as custom code in the head section</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</span>
                        <div>
                          <p className="text-gray-900 font-medium">Publish your site</p>
                          <p className="text-gray-600 text-sm">Save and publish to make your llms.txt live</p>
                        </div>
                      </div>
                    </>
                  )}

                  {selectedPlatform === 'webflow' && (
                    <>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</span>
                        <div>
                          <p className="text-gray-900 font-medium">Open your Webflow project</p>
                          <p className="text-gray-600 text-sm">Go to <strong>Project Settings → Custom Code</strong></p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</span>
                        <div>
                          <p className="text-gray-900 font-medium">Add your llms.txt content</p>
                          <p className="text-gray-600 text-sm">Paste the content in the head section</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</span>
                        <div>
                          <p className="text-gray-900 font-medium">Publish your site</p>
                          <p className="text-gray-600 text-sm">Publish to make your llms.txt live</p>
                        </div>
                      </div>
                    </>
                  )}

                  {selectedPlatform === 'squarespace' && (
                    <>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</span>
                        <div>
                          <p className="text-gray-900 font-medium">Go to your Squarespace dashboard</p>
                          <p className="text-gray-600 text-sm">Navigate to <strong>Settings → Advanced → Code Injection</strong></p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</span>
                        <div>
                          <p className="text-gray-900 font-medium">Add your llms.txt content</p>
                          <p className="text-gray-600 text-sm">Paste the content in the header section</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</span>
                        <div>
                          <p className="text-gray-900 font-medium">Save changes</p>
                          <p className="text-gray-600 text-sm">Save and your llms.txt will be live</p>
                        </div>
                      </div>
                    </>
                  )}

                  {selectedPlatform === 'other' && (
                    <>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</span>
                        <div>
                          <p className="text-gray-900 font-medium">Access your file manager</p>
                          <p className="text-gray-600 text-sm">Use your hosting provider's file manager or FTP client</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</span>
                        <div>
                          <p className="text-gray-900 font-medium">Upload to root directory</p>
                          <p className="text-gray-600 text-sm">Upload your <code className="bg-gray-100 px-1 rounded">llms.txt</code> file to your website's root directory</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</span>
                        <div>
                          <p className="text-gray-900 font-medium">Test your file</p>
                          <p className="text-gray-600 text-sm">Visit <code className="bg-gray-100 px-1 rounded">yoursite.com/llms.txt</code> to verify it's working</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      <span>File must be named exactly <code className="bg-gray-100 px-1 rounded">llms.txt</code></span>
                    </div>
                          {selectedPlatform !== 'other' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const guides = {
                                  wordpress: 'https://www.wpbeginner.com/wp-tutorials/what-is-llms-txt-how-to-add-llms-txt-in-wordpress/',
                                  shopify: 'https://www.linkedin.com/posts/kurtelster_you-can-add-an-llmstxt-file-to-your-shopify-activity-7340400273045000192-arzW',
                                  wix: 'https://forum.wixstudio.com/t/how-to-add-a-llms-txt-to-my-wix-website/71520',
                                  webflow: 'https://help.webflow.com/hc/en-us/articles/43240104183315-Upload-an-llms-txt-file-to-your-site',
                                  squarespace: 'https://forum.squarespace.com/topic/336609-llmstxt/'
                                };
                                if (guides[selectedPlatform as keyof typeof guides]) {
                                  window.open(guides[selectedPlatform as keyof typeof guides], '_blank');
                                }
                              }}
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Guide
                            </Button>
                          )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
